import * as React from "react";
import { useState } from "react";
import styled, { css } from "styled-components";
import { useNavigate } from "react-router-dom";
import SecurityRoutes from "security-app/routing/Routes";
import { relativeDifferenceSeconds } from "util/DateTime";
import useUserDateTime from "hooks/useUserDateTime";
import { IconButton, Drawer } from "components/common";
import { Row, Col } from "security-app/components/common/FormBaseStyles";
import { usePagination, useSetPagination, useModalContext } from "common/contexts";
import VulnerabilitiesCount from "security-app/components/Assets/AssetDetails/VulnerabilitiesCount";
import SecurityEventsCount from "security-app/components/Assets/AssetDetails/SecurityEventsCount";
import { useGetSecurityEvents } from "security-app/hooks/useSecurityEventsAPI";
import useAssetIdSearchParam from "security-app/hooks/useAssetIdSearchParam";
import Details from "./Details";
import Vulnerabilities from "./Vulnerabilities";
import SecurityEvents from "./SecurityEvents";
const NavTab = styled.span(
  ({ $active, theme }) => css`
    cursor: pointer;
    color: ${$active ? `${theme.colors.text.primary}` : "grey"};
    padding: 0.5rem;
    border-bottom: 1px solid ${$active ? `${theme.colors.button.primary.background}` : "transparent"};

    &:hover {
      color: ${theme.colors.text.primary};
    }
  `
);
const DrawerTitle = ({ asset, hideEdit }) => {
  const { setModal, setEntity } = useModalContext();
  const { saveAssetIdSearchParam } = useAssetIdSearchParam();
  const navigate = useNavigate();
  const onEdit = () => {
    setEntity({ asset, fromDetails: true });
    setModal(asset.details.type === "machine" ? "MACHINE-FORM" : "USER-FORM");
  };
  const showAssetDashboard = () => {
    saveAssetIdSearchParam(asset.id);
    navigate(`${SecurityRoutes.ACTIVITY.ASSET_DRILLDOWN}?assetId=${asset.id}`);
  };
  return /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement("span", null, asset.name, " Details"), /* @__PURE__ */ React.createElement(IconButton, { title: "Show Asset Dashboard", iconType: "regular", name: "dashboard", onClick: showAssetDashboard }), !hideEdit && /* @__PURE__ */ React.createElement(IconButton, { title: "Edit Asset", iconType: "regular", name: "edit_square", onClick: onEdit }));
};
const VulnerabilitiesTabLabel = ({ vulnerabilities = [] }) => /* @__PURE__ */ React.createElement(Row, { $gap: "0.5rem" }, /* @__PURE__ */ React.createElement("span", null, "Vulnerabilities"), /* @__PURE__ */ React.createElement(VulnerabilitiesCount, { vulnerabilities }));
const SecurityEventsLabel = ({ events = null }) => /* @__PURE__ */ React.createElement(Row, { $gap: "0.5rem" }, /* @__PURE__ */ React.createElement("span", null, "Security Events"), /* @__PURE__ */ React.createElement(SecurityEventsCount, { events }));
const tabs = [
  {
    id: "details",
    Label: () => /* @__PURE__ */ React.createElement("span", null, "Details"),
    Component: Details,
    type: ["machine", "user"]
  },
  {
    id: "security_events",
    Label: SecurityEventsLabel,
    Component: SecurityEvents,
    type: ["machine", "user"]
  },
  {
    id: "vulnerabilities",
    Label: VulnerabilitiesTabLabel,
    Component: Vulnerabilities,
    type: ["machine"]
  }
];
function AssetDetailsDrawer({ asset, hideEdit = false, onClose }) {
  const [activeTab, setActiveTab] = useState(0);
  const localPagination = usePagination();
  const setLocalPagination = useSetPagination();
  const { userTimezone } = useUserDateTime();
  const { securityEvents, pagination } = useGetSecurityEvents(
    {
      ...localPagination,
      filters: {
        alerts: "include",
        timerange: relativeDifferenceSeconds(asset.created_at),
        associated_assets: [asset.id]
      }
    },
    userTimezone
  );
  const handleChangeTab = (newTab) => {
    setActiveTab(newTab);
  };
  const onPageChange = (newPage, newPageSize) => {
    setLocalPagination({ ...localPagination, page: newPage, perPage: newPageSize });
  };
  const { Component } = tabs[activeTab];
  const tabsForCurrentAssetType = tabs.filter((tab) => tab.type.includes(asset.details.type));
  return /* @__PURE__ */ React.createElement(
    Drawer,
    {
      onClose,
      size: "lg",
      position: "right",
      overlayProps: { zIndex: "1030" },
      title: /* @__PURE__ */ React.createElement(DrawerTitle, { asset, hideEdit })
    },
    /* @__PURE__ */ React.createElement(Col, null, /* @__PURE__ */ React.createElement(Row, null, tabsForCurrentAssetType.map((tab, index) => /* @__PURE__ */ React.createElement(
      NavTab,
      {
        key: tab.id,
        $active: activeTab === index,
        onClick: () => handleChangeTab(index),
        role: "tab",
        "aria-label": tab.id
      },
      /* @__PURE__ */ React.createElement(tab.Label, { vulnerabilities: asset.vulnerabilities, events: pagination.total })
    ))), /* @__PURE__ */ React.createElement(Component, { asset, securityEvents, pagination, onPageChange }))
  );
}
export default AssetDetailsDrawer;
